import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Input,
  InputDecoration,
  InputGroup,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { SearchIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { DropdownSelectProps, Option } from './DropdownSelect.types';

export type { Option, DropdownSelectProps };

const DropdownSelect = <T extends string | number>(props: DropdownSelectProps<T>) => {
  const { t } = useTranslation('common');

  const {
    description,
    options,
    label,
    initialValue,
    isMultiSelect,
    onChange,
    enableSearch,
    singleIconButton = false,
    icon,
    disabled = false,
    menuPosition = 'above',
    keepLabelStatic = false,
    endIcon,
  } = props;

  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(initialValue);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const isControlled = 'value' in props;
  const value = isControlled ? props.value : internalValue;

  const selectedCount = useMemo(() => {
    if (isMultiSelect !== true) return 0;
    return Array.isArray(value) ? value.length : 0;
  }, [isMultiSelect, value]);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setSearch('');
  }, []);

  const handleSelectedOption = useCallback(
    (optionValue: T) => {
      if (isMultiSelect === true) {
        const currentSelectedOptions = Array.isArray(value) ? value : [];
        const isDeselecting = currentSelectedOptions.includes(optionValue);
        const newSelectedOptions = isDeselecting
          ? currentSelectedOptions.filter((m) => m !== optionValue)
          : [...currentSelectedOptions, optionValue];

        if (!isControlled) setInternalValue(newSelectedOptions);
        (onChange as ((options: T[]) => void) | undefined)?.(newSelectedOptions);
      } else {
        if (!isControlled) setInternalValue(optionValue);
        (onChange as ((option: T) => void) | undefined)?.(optionValue);
        handleOpenChange(false);
      }
    },
    [isMultiSelect, value, onChange, handleOpenChange, isControlled],
  );

  const clearAll = useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      event?.preventDefault();
      if (isMultiSelect === true) {
        if (!isControlled) setInternalValue([] as unknown as typeof initialValue);
        (onChange as ((options: T[]) => void) | undefined)?.([]);
      } else {
        if (!isControlled) setInternalValue('' as unknown as typeof initialValue);
        (onChange as ((option: T) => void) | undefined)?.(undefined as unknown as T);
      }
    },
    [isMultiSelect, isControlled, onChange],
  );

  const filteredOptions = useMemo(() => {
    if (enableSearch !== true || search.trim() === '') return options;
    const query = search.toLowerCase();
    return options.filter((option) => {
      const inLabel = option.label.toLowerCase().includes(query);
      const inSecondary = (option.secondaryLabel ?? '').toLowerCase().includes(query);
      return inLabel || inSecondary;
    });
  }, [options, enableSearch, search]);

  const selectedOptionLabel = useMemo(() => {
    if (isMultiSelect === true) {
      if (!Array.isArray(value)) return label;
      if (keepLabelStatic) return label;
      const selected = options.filter((m) => value.includes(m.value));
      if (selected.length === 0) return label;
      if (selected.length === 1) return selected[0].label;
      return `${selected[0].label} + ${selected.length - 1}`;
    }

    return options.find((m) => m.value === value)?.label ?? label;
  }, [isMultiSelect, label, options, value, keepLabelStatic]);

  const isSelected = (optionValue: T) => {
    if (isMultiSelect === true) return Array.isArray(value) && value.includes(optionValue);
    return value === optionValue;
  };

  const triggerName = typeof label === 'string' ? label : t('components.dropdownSelect.openMenu');
  const showSearch = enableSearch === true && isMultiSelect === true;

  const trigger = singleIconButton ? (
    <div
      className={`group inline-flex items-center shadow-sm ${disabled ? 'opacity-40' : ''} ${
        selectedCount > 0
          ? 'h-8 gap-1.5 rounded-full border border-border bg-accent px-2'
          : 'size-8 justify-center rounded-full bg-card'
      }`}
    >
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="ghost"
          size="icon-mini"
          roundness="full"
          disabled={disabled}
          aria-label={triggerName}
          className="shrink-0 text-muted-foreground"
        >
          <span aria-hidden="true" className="inline-flex items-center justify-center leading-none">
            {icon}
          </span>
        </Button>
      </DropdownMenuTrigger>
      {selectedCount > 0 && (
        <>
          <span className="text-[15px] text-foreground">{selectedCount}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-mini"
            roundness="full"
            onClick={clearAll}
            aria-label={t('components.dropdownSelect.clearSelection')}
            className="shrink-0 text-muted-foreground opacity-0 transition-opacity duration-fast ease-out group-hover:opacity-100 focus-visible:opacity-100"
          >
            <XIcon className="size-4" />
          </Button>
        </>
      )}
    </div>
  ) : (
    <DropdownMenuTrigger asChild disabled={disabled}>
      <Button type="button" variant="outline" disabled={disabled}>
        <span className="inline-block max-w-50 truncate align-middle">{selectedOptionLabel}</span>
        {endIcon}
      </Button>
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      {description === '' ? (
        trigger
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent side="top">{description}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <DropdownMenuContent
        align="start"
        side={menuPosition === 'below' ? 'bottom' : 'top'}
        className="pointer-events-auto flex max-h-112.5 w-65 flex-col overflow-hidden"
        // Radix moves focus onto the menu container itself on open, which would
        // strand the search field the user is about to type into. The
        // target/currentTarget guard keeps the redirect from re-firing on the
        // focusin the input itself bubbles back up.
        onFocus={(event) => {
          if (!showSearch) return;
          if (event.target !== event.currentTarget) return;
          searchInputRef.current?.focus();
        }}
      >
        <div className="mb-3 flex items-center justify-between gap-2 px-1 pt-2">
          <span className="text-base text-muted-foreground">
            {label ?? t('components.dropdownSelect.select')}
          </span>
          {isMultiSelect === true && (
            <Button type="button" size="sm" variant="outline" onClick={clearAll}>
              {t('components.dropdownSelect.clear')}
            </Button>
          )}
        </div>

        {showSearch && (
          <div className="mb-2 px-1">
            <InputGroup size="sm">
              <InputDecoration muted>
                <SearchIcon />
              </InputDecoration>
              <Input
                ref={searchInputRef}
                value={search}
                aria-label={t('components.dropdownSelect.search')}
                placeholder={t('components.dropdownSelect.search')}
                onChange={(event) => setSearch(event.target.value)}
                // Radix's menu typeahead and arrow navigation live on the
                // content element; without this every keystroke would jump the
                // highlight instead of reaching the field.
                onKeyDown={(event) => event.stopPropagation()}
              />
              {search !== '' && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-mini"
                  aria-label={t('components.dropdownSelect.clearSearch')}
                  className="shrink-0"
                  onClick={() => {
                    setSearch('');
                    searchInputRef.current?.focus();
                  }}
                >
                  <XIcon className="size-4" />
                </Button>
              )}
            </InputGroup>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {isMultiSelect === true ? (
            filteredOptions.map((option) => (
              <DropdownMenuCheckboxItem
                key={String(option.value)}
                checked={isSelected(option.value)}
                // Keep the menu open so a user can tick several options.
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={() => handleSelectedOption(option.value)}
              >
                <OptionBody option={option} />
              </DropdownMenuCheckboxItem>
            ))
          ) : (
            <DropdownMenuRadioGroup
              value={value === undefined ? '' : String(value)}
              onValueChange={(next) => {
                const match = filteredOptions.find((option) => String(option.value) === next);
                if (match) handleSelectedOption(match.value);
              }}
            >
              {filteredOptions.map((option) => (
                <DropdownMenuRadioItem key={String(option.value)} value={String(option.value)}>
                  <OptionBody option={option} />
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          )}
          {filteredOptions.length === 0 && (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              {t('components.dropdownSelect.noResults')}
            </p>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

function OptionBody<T>({ option }: { option: Readonly<Option<T>> }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      {option.icon !== undefined && option.icon !== null && (
        <span className="flex shrink-0 items-center">{option.icon}</span>
      )}
      <span className="flex min-w-0 flex-col">
        <span className="truncate">{option.label}</span>
        {option.secondaryLabel !== undefined && (
          <span className="truncate text-xs text-muted-foreground">{option.secondaryLabel}</span>
        )}
      </span>
    </span>
  );
}

export default DropdownSelect;
